import { Form, useFormContext } from "@/components/form";
import { getAllClientes } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { Stack } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Dialog, Drawer, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import { AddClienteMutation } from "@/features/auxiliar/clientes/api/add-cliente";
import { AddSimpleClienteMutation } from "@/features/auxiliar/clientes/api/add-simple-cliente";
import AddClienteForm from "@/features/auxiliar/clientes/components/add-cliente-form";
import SimpleClienteForm, { simpleClienteFormSchema } from "@/features/auxiliar/clientes/components/simple-cliente-form";
import { notify } from "@/shared/notification";
import VentaClienteManualSearch from "./venta-cliente-manual-search";
import { getVendedor } from "@/features/auxiliar/vendedores/api/get-vendedor";
import { getAllVendedores } from "@/features/auxiliar/vendedores/api/get-all-vendedores";
import { SchemaFormHook } from "@/components/form";
import { addClienteFormSchema } from "@/features/auxiliar/clientes/components/add-cliente-form.schema";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { OperationalMode } from "@/shared/types/operational-mode";

export default function VentaGeneralInfoFields({
    nextInputRef
}: {
    nextInputRef?: React.MutableRefObject<any>
}) {
    const openModal = useOpenModal();
    const { facturacionEnabled, mode } = useOperationalMode();
    const form = useFormContext();

    // Determinar si es modo simplificado (modo 1 = Gestion)
    const isSimpleMode = mode === OperationalMode.Gestion;
    // Si es modo 2 o 3, usar el modo completo
    const isFullMode = mode === OperationalMode.GestionConFacturacion || mode === OperationalMode.Facturacion;

    return <Stack gap={2} direction={"row"} flexWrap={"wrap"}>
        <Stack flex={"2 1 150px"}>
            <Form.ModelComboBox name="vendedorId" label="Vendedor" get={getVendedor} getAll={getAllVendedores} size="small" />
        </Stack>
        <Stack flex={"8 1 150px"} direction={"row"} gap={1}>
            <Stack flex={1}>
                <Form.ModelComboBox
                    name="clienteId"
                    label="Cliente"
                    get={getCliente}
                    getAll={getAllClientes}
                    size="small"
                    onObjectSelected={(cliente) => {
                        if (cliente && facturacionEnabled && cliente.naturaleza == null) {
                            notify("El cliente seleccionado no tiene los datos necesarios. Por favor, actualice los datos del cliente para poder generar una venta.", { variant: "error" });
                            form.setFieldValue("clienteId", null);
                        }
                    }}
                />
            </Stack>
            <Form.IconButton onClick={() => openModal("addCliente")}><AddIcon /></Form.IconButton>
            <Form.IconButton onClick={() => openModal("searchCliente")}><SearchIcon /></Form.IconButton>
        </Stack>
        <Stack flex={"1 1 auto"}>
            <Form.DatePicker name="fecha" label="Fecha" size="small" onKeyDown={e => {
                if (e.key == "Enter" && !e.shiftKey && nextInputRef?.current) {
                    e.preventDefault();
                    (nextInputRef.current as HTMLElement).focus();
                }
            }} />
        </Stack>

        {isSimpleMode && (
            <Drawer code="addCliente">
                <ModalHeader>Agregar Cliente</ModalHeader>
                <AddSimpleClienteMutation>
                    {mutation => <SchemaFormHook
                        schema={simpleClienteFormSchema}
                        onSubmit={async ({ value }) => {
                            const cliente = await mutation.mutateAsync(value);
                            notify("El cliente se ha agregado correctamente.");
                            form.setFieldValue("clienteId", cliente.id);
                        }}
                        closeModalOnSubmit
                        submitOnce
                    >
                        {formCliente => <SimpleClienteForm form={formCliente} padding={3} />}
                    </SchemaFormHook>}
                </AddSimpleClienteMutation>
            </Drawer>
        )}

        {isFullMode && (
            <Dialog code="addCliente" size="md">
                <ModalHeader>Agregar Cliente</ModalHeader>
                <AddClienteMutation>
                    {mutation => <SchemaFormHook
                        schema={addClienteFormSchema}
                        onSubmit={async ({ value }) => {
                            const cliente = await mutation.mutateAsync(value);
                            notify("El cliente se ha agregado correctamente.");
                            form.setFieldValue("clienteId", cliente.id);
                        }}
                        closeModalOnSubmit
                        submitOnce
                    >
                        {formCliente => <AddClienteForm form={formCliente} padding={3} />}
                    </SchemaFormHook>}
                </AddClienteMutation>
            </Dialog>
        )}

        <Dialog code="searchCliente" size="lg">
            <ModalHeader>Buscar Cliente</ModalHeader>
            <ModalContextExtractor>
                {({ handleClose }) => <VentaClienteManualSearch
                    padding={3}
                    onClienteSelect={cliente => {
                        if (facturacionEnabled && cliente.naturaleza == null) {
                            notify("El cliente seleccionado no tiene los datos necesarios. Por favor, actualice los datos del cliente para poder generar una venta.", { variant: "error" });
                        } else {
                            form.setFieldValue("clienteId", cliente.id);
                            handleClose();
                        }
                    }}
                />}
            </ModalContextExtractor>
        </Dialog>
    </Stack>
}